/**
 * The single wording for "the request never reached the server" — a dropped,
 * aborted or timed-out request. Every HTTP layer in the portal (RTK Query,
 * apiFetch, axios) normalizes to this, so a member never reads a DOMException
 * such as "AbortError: signal is aborted without reason".
 */
export const NETWORK_ERROR_MESSAGE =
  "Network is slow or unavailable. Please check your connection and try again.";

/** Does this message/cause describe a dropped request rather than a server reply? */
export function isNetworkFailureMessage(msg?: string | null): boolean {
  return /AbortError|TimeoutError|CanceledError|Failed to fetch|NetworkError|Network Error|network error|ERR_NETWORK|ECONNABORTED|signal is aborted/i.test(
    String(msg ?? ""),
  );
}

/**
 * Turn an RTK Query error into a human-readable message that reveals the real
 * cause — including network/timeout/CORS failures and non-JSON server errors,
 * which otherwise collapse into an unhelpful generic fallback.
 */
export function describeApiError(e: any, fallback = "Something went wrong."): string {
  if (!e) return fallback;

  // Network / timeout / aborted — no HTTP response reached us. Never show the
  // underlying DOMException text ("signal is aborted without reason"); it reads
  // as a crash to the member and tells them nothing they can act on.
  if (
    e.status === "FETCH_ERROR" ||
    e.status === "TIMEOUT_ERROR" ||
    isNetworkFailureMessage(`${e.error ?? ""} ${e.originalError ?? ""} ${e.message ?? ""}`)
  ) {
    return NETWORK_ERROR_MESSAGE;
  }
  if (e.status === "PARSING_ERROR") {
    return `Server returned an unexpected response (HTTP ${e.originalStatus ?? "?"}). It may be misconfigured — try again or contact support.`;
  }

  const data = e.data;

  // Standard Laravel JSON error.
  if (data?.message) {
    // Surface the first validation error too, if present.
    if (data.errors && typeof data.errors === "object") {
      const first: any = Object.values(data.errors)[0];
      const detail = Array.isArray(first) ? first[0] : first;
      if (detail && detail !== data.message) return `${data.message} (${detail})`;
    }
    return data.message;
  }

  // Non-JSON body (e.g. an HTML error page) with an HTTP status.
  if (typeof e.status === "number") {
    if (e.status === 401) return "Your session has expired. Please sign in again.";
    if (e.status === 403) return "You don't have permission to do that.";
    if (e.status === 419) return "Your session expired (CSRF). Please refresh and try again.";
    if (e.status === 404) return "That record could not be found.";
    if (e.status >= 500) return `Server error (HTTP ${e.status}). Please try again or contact support.`;
    return `Request failed (HTTP ${e.status}).`;
  }

  if (typeof data === "string" && data.trim()) return data.slice(0, 200);
  return e.message || fallback;
}
